import React, { useState } from 'react';
import axios from 'axios';
import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
  Snackbar,
  MenuItem,
  Link as MuiLink,
} from '@mui/material';
import { Diamond as DiamondIcon } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import baseURL from '../../baseURL';

const LICENSE_OPTIONS = [
  { value: '7days', label: '7 days' },
  { value: '15days', label: '15 days' },
  { value: '1month', label: '1 month' },
  { value: '1year', label: '1 year' },
  { value: '0', label: 'Lifetime' },
];

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [form, setForm] = useState({
    name: '',
    companyName: '',
    address: '',
    licenseNumber: '',
    mobileNumber: '',
    password: '',
    blackPassword: '',
    adminPassword: '',
    emergencyPassword: '',
    licenseDuration: '1year',
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const response = await axios.post(`${baseURL}/auth/signup`, form, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 20000,
      });
      const { token, accessLevel = 'user' } = response.data;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('userType', accessLevel);
      }
      navigate('/');
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Signup failed';
      setError(errorMessage);
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 4,
        background:
          'radial-gradient(900px 480px at 15% 10%, rgba(201,162,77,0.28), transparent 55%), radial-gradient(700px 420px at 90% 80%, rgba(30,58,95,0.35), transparent 50%), linear-gradient(145deg, #0b1220 0%, #152238 45%, #0f172a 100%)',
      }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          {error}
        </Alert>
      </Snackbar>

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 560,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          border: '1px solid rgba(240,217,160,0.28)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, #f8fafc 100%)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 1.5,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              background: 'linear-gradient(145deg, #f0d9a0, #c9a24d)',
              color: '#1a1408',
            }}
          >
            <DiamondIcon />
          </Box>
          <Typography sx={{ fontWeight: 850, fontSize: 24, letterSpacing: '0.12em', color: '#0f172a' }}>
            SIGN UP (VPSB)
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 13, color: '#64748b' }}>
            Legal password syncs to VPSA. Black / admin / emergency stay on VPSB only.
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 1.75 }}>
          <TextField required name="name" label="Name" value={form.name} onChange={onChange} fullWidth />
          <TextField required name="companyName" label="Company name" value={form.companyName} onChange={onChange} fullWidth />
          <TextField required name="address" label="Address" value={form.address} onChange={onChange} fullWidth multiline minRows={2} />
          <TextField required name="licenseNumber" label="License number" value={form.licenseNumber} onChange={onChange} fullWidth />
          <TextField required name="mobileNumber" label="Mobile number" value={form.mobileNumber} onChange={onChange} fullWidth />
          <TextField
            required
            select
            name="licenseDuration"
            label="License duration"
            value={form.licenseDuration}
            onChange={onChange}
            fullWidth
          >
            {LICENSE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            name="password"
            label="Legal password (also sent to VPSA)"
            type="password"
            value={form.password}
            onChange={onChange}
            fullWidth
          />
          <TextField
            required
            name="blackPassword"
            label="Black password (VPSB dashboard)"
            type="password"
            value={form.blackPassword}
            onChange={onChange}
            fullWidth
          />
          <TextField
            required
            name="adminPassword"
            label="Admin password"
            type="password"
            value={form.adminPassword}
            onChange={onChange}
            fullWidth
          />
          <TextField
            required
            name="emergencyPassword"
            label="Emergency password"
            type="password"
            value={form.emergencyPassword}
            onChange={onChange}
            fullWidth
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.35,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 750,
              background: 'linear-gradient(120deg, #0f172a 0%, #1e3a5f 100%)',
            }}
          >
            {loading ? 'Creating…' : 'Create account'}
          </Button>
        </Box>

        <Typography sx={{ mt: 2.5, textAlign: 'center', fontSize: 13, color: '#64748b' }}>
          Already have an account?{' '}
          <MuiLink component={Link} to="/login" underline="hover" sx={{ color: '#1e3a5f', fontWeight: 600 }}>
            Sign in
          </MuiLink>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Signup;
